import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { diffRuleSentences } from '../../helpers/businessRuleDiff';

interface DescriptionCardsProps {
  descriptions: string[];
  loading?: boolean;
  selectedDescription?: string;
  onSelect: (description: string) => void;
  onEdit: (description: string) => void;
  disabled?: boolean;
  loadingMessage?: string;
  updatingMessage?: string;
  emptyMessage?: string;
  suggestionLabel?: string;
  /** When set, sentences in a card that do not occur in this text are rendered bold (used by
   * "Improve my rules" to show what is new). Omit to render cards exactly as before. */
  highlightBaseline?: string;
  /** Overrides the "{suggestionLabel} #1" header for the first card only (e.g. "Current
   * rules" for an existing value shown alongside fresh suggestions). Omit to keep every
   * existing caller's numbering exactly as it is today. */
  firstCardLabel?: string;
}

const DescriptionCards: React.FC<DescriptionCardsProps> = ({
  descriptions,
  loading = false,
  selectedDescription,
  onSelect,
  onEdit,
  disabled = false,
  loadingMessage = 'Fetching the suggestions for your channel description',
  updatingMessage = 'Updating suggestions...',
  emptyMessage = 'No description suggestions available.',
  suggestionLabel = 'Suggestion',
  highlightBaseline,
  firstCardLabel,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  
  // If loading and no descriptions, show loading state
  if (loading && descriptions.length === 0) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          py: 4,
          gap: 2,
          height: '200px', // Fixed height to prevent scrolling
          minHeight: '200px',
          maxHeight: '200px',
        }}
      >
        <CircularProgress size={40} />
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          {loadingMessage}
        </Typography>
      </Box>
    );
  }

  // Don't show "no suggestions" message if we're loading - keep previous content visible
  if (descriptions.length === 0 && !loading) {
    return (
      <Box sx={{ py: 2, height: '120px', minHeight: '120px', maxHeight: '120px' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        py: 1,
        position: 'relative',
        height: loading && descriptions.length > 0 ? '300px' : 'auto', // Fixed height when loading with existing suggestions
        minHeight: loading && descriptions.length > 0 ? '300px' : 'auto',
        maxHeight: loading && descriptions.length > 0 ? '300px' : 'none',
        overflow: loading && descriptions.length > 0 ? 'hidden' : 'visible',
      }}
    >
      {/* Show loading overlay if loading with existing descriptions */}
      {loading && descriptions.length > 0 && (
        <Box
          sx={(theme) => ({
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
            zIndex: 1,
            borderRadius: 1,
          })}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              {updatingMessage}
            </Typography>
          </Box>
        </Box>
      )}
      
      {descriptions.map((description, index) => {
        const isSelected = selectedDescription?.trim() === description.trim();
        const isHovered = hoveredIndex === index;
        return (
          <Card
            key={index}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            sx={{
              transition: 'all 0.2s ease-in-out',
              border: '2px solid',
              borderColor: isSelected ? 'primary.main' : 'divider',
              borderRadius: 2,
              boxShadow: isSelected ? 2 : 'none',
              backgroundColor: isSelected 
                ? (theme) => theme.palette.mode === 'dark' 
                  ? 'rgba(28, 171, 222, 0.1)' 
                  : 'rgba(28, 171, 222, 0.05)'
                : 'background.paper',
              display: 'block',
              height: 'auto',
              minHeight: 'unset',
              maxHeight: 'none',
              overflow: 'visible',
              '& .MuiCardContent-root': {
                height: 'auto',
                maxHeight: 'none',
                overflow: 'visible',
              },
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: 2,
                transform: 'translateY(-1px)',
                backgroundColor: isSelected
                  ? (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(28, 171, 222, 0.15)'
                    : 'rgba(28, 171, 222, 0.08)'
                  : 'background.paper',
              },
            }}
          >
            <CardContent 
              sx={{ 
                p: 1.5, 
                '&:last-child': { pb: 1.5 },
                height: 'auto',
                maxHeight: 'none',
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {/* Header with suggestion number and button */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  {index === 0 && firstCardLabel
                    ? firstCardLabel
                    : `${suggestionLabel} #${firstCardLabel ? index : index + 1}`}
                </Typography>
                <Box
                  sx={{
                    flexShrink: 0,
                  }}
                >
                  <Button
                    variant={isSelected ? "contained" : "outlined"}
                    size="small"
                    startIcon={isSelected ? <CheckCircleIcon /> : null}
                    onClick={() => !disabled && onEdit(description)}
                    disabled={disabled}
                    sx={{
                      textTransform: 'none',
                      minWidth: '100px',
                      ...(isSelected ? {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                          color: 'white',
                        },
                      } : {
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        backgroundColor: 'transparent',
                        ...(isHovered && !disabled ? {
                          backgroundColor: 'primary.main',
                          borderColor: 'primary.main',
                          color: 'white',
                        } : {}),
                        '&:hover': {
                          backgroundColor: disabled ? 'transparent' : 'primary.main',
                          borderColor: 'primary.main',
                          color: disabled ? 'primary.main' : 'white',
                        },
                      }),
                    }}
                  >
                    {isSelected ? 'Selected' : 'Select'}
                  </Button>
                </Box>
              </Box>
              
              {/* Description below header */}
              <Box sx={{ width: '100%' }}>
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    color: 'text.primary',
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-line', // Preserve newlines from \n characters
                    overflowWrap: 'break-word',
                    display: 'block',
                  }}
                >
                  {highlightBaseline?.trim()
                    ? diffRuleSentences(description, highlightBaseline).map((segment, segmentIndex) =>
                        segment.isNew ? (
                          <Box key={segmentIndex} component="strong" sx={{ fontWeight: 700 }}>
                            {segment.text}
                          </Box>
                        ) : (
                          <React.Fragment key={segmentIndex}>{segment.text}</React.Fragment>
                        ),
                      )
                    : description}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default DescriptionCards;
